import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useSync } from "@/context/SyncContext";
import { useLang } from "@/context/LanguageContext";

const IGNORED_FIELDS = new Set(["id", "_id", "_offline", "updated_at", "created_at", "user_id", "ref_code"]);

const fmt = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

function diffFields(mine = {}, server = {}) {
  const keys = new Set([...Object.keys(mine || {}), ...Object.keys(server || {})]);
  const rows = [];
  for (const k of keys) {
    if (IGNORED_FIELDS.has(k)) continue;
    const a = mine?.[k];
    const b = server?.[k];
    if (JSON.stringify(a) !== JSON.stringify(b)) rows.push({ field: k, mine: fmt(a), server: fmt(b) });
  }
  return rows.slice(0, 12); // keep the dialog readable
}

/** Shown automatically when sync detects that a record changed on the server
 *  while it was also edited offline — user picks which version to keep. */
export default function ConflictDialog() {
  const sync = useSync();
  const { t } = useLang();
  if (!sync || sync.conflicts.length === 0) return null;

  const conflict = sync.conflicts[0]; // resolve one at a time, clearly
  const name = conflict.mine?.name || conflict.server?.name || conflict.mine?.subject || conflict.url;
  const rows = diffFields(conflict.mine, conflict.server);
  const serverDeleted = !conflict.server;

  return (
    <Dialog open onOpenChange={() => { /* must resolve explicitly */ }}>
      <DialogContent className="max-w-lg bg-white" data-testid="conflict-dialog" hideClose>
        <DialogHeader>
          <DialogTitle>{t("conflictTitle")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-stone-600">
            {t("conflictExplain")}
            <span className="block mt-1 font-medium text-stone-900">{name}</span>
            {sync.conflicts.length > 1 && (
              <span className="block mt-1 text-xs text-stone-400">+{sync.conflicts.length - 1} more</span>
            )}
          </p>
          {serverDeleted ? (
            <p className="text-sm text-amber-700 bg-amber-50 rounded-lg p-3">{t("recordDeletedOnServer")}</p>
          ) : (
            rows.length > 0 && (
              <div className="border border-stone-200 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-stone-50 text-stone-500">
                    <tr>
                      <th className="text-left px-3 py-1.5 font-medium">{t("conflictField")}</th>
                      <th className="text-left px-3 py-1.5 font-medium">{t("mineLabel")}</th>
                      <th className="text-left px-3 py-1.5 font-medium">{t("serverLabel")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.field} className="border-t border-stone-100">
                        <td className="px-3 py-1.5 text-stone-500">{r.field}</td>
                        <td className="px-3 py-1.5 text-stone-900 break-all">{r.mine}</td>
                        <td className="px-3 py-1.5 text-stone-900 break-all">{r.server}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
          <div className="flex flex-col sm:flex-row justify-end gap-2 pt-1">
            <Button
              data-testid="conflict-keep-server"
              variant="outline"
              className="border-stone-300"
              onClick={() => sync.resolveConflict(conflict, "server")}
            >
              {t("keepServer")}
            </Button>
            <Button
              data-testid="conflict-keep-mine"
              className="bg-stone-900 hover:bg-stone-800 text-white"
              onClick={() => sync.resolveConflict(conflict, "mine")}
            >
              {t("keepMine")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
